// js/common/animation.js
import { gsap, ScrollTrigger } from "./gsap";

/**
 * =========================================================
 * Reusable animation module (pruned)
 * - Supports only:
 *   .js-fade-up
 *   .js-slide-left
 *   .js-chars
 *   .js-split-y
 *   .js-clip-scroll
 *   .js-text-random
 *   .js-rise-onload
 * =========================================================
 */

/* =========================
   Utilities (exported)
========================= */

/** 一要素につき一回だけ初期化させるためのガード */
export function guardOnce(el, key) {
  const attr = "data-anim-" + key;
  if (el.hasAttribute(attr)) return false;
  el.setAttribute(attr, "1");
  return true;
}

/** 文字分割（日本語対応・<br>維持・再実行防止） */
export function splitIntoChars(el) {
  if (!el || el.dataset.splitted === "true") return;

  const walker = document.createTreeWalker(
    el,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
  );
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  const useSegmenter = "Segmenter" in Intl;
  const segmenter = useSegmenter
    ? new Intl.Segmenter("ja", { granularity: "grapheme" })
    : null;

  nodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) return;
    if (node.nodeType !== Node.TEXT_NODE) return;

    const text = node.nodeValue;
    if (!text || !text.trim()) return;

    const parent = node.parentNode;
    const frag = document.createDocumentFragment();

    const pieces = useSegmenter
      ? Array.from(segmenter.segment(text), (s) => s.segment)
      : Array.from(text);

    pieces.forEach((ch) => {
      if (/\s/.test(ch)) {
        frag.appendChild(document.createTextNode(ch));
      } else {
        const span = document.createElement("span");
        span.className = "char";
        span.textContent = ch;
        frag.appendChild(span);
      }
    });

    parent.replaceChild(frag, node);
  });

  el.dataset.splitted = "true";
}

/** ScrollTrigger.refresh を安全に呼ぶ */
function refreshST() {
  if (ScrollTrigger?.refresh) ScrollTrigger.refresh();
  else if (window.ScrollTrigger?.refresh) window.ScrollTrigger.refresh();
}

/** セレクタのスコープ検索 */
function $$(scope, selector) {
  return (scope || document).querySelectorAll(selector);
}

/* =========================
   Effects (exported)
========================= */

/** Fade up */
export function initFadeUp({ scope } = {}) {
  $$(scope, ".js-fade-up").forEach((el) => {
    if (!guardOnce(el, "fadeUp")) return;
    gsap.to(el, {
      scrollTrigger: { trigger: el, start: "top 80%" },
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out",
    });
  });
}

/** Slide in（左のみ）: data-start で開始位置調整可 */
export function initSlideIn({ scope } = {}) {
  $$(scope, ".js-slide-left").forEach((el) => {
    if (!guardOnce(el, "slideLeft")) return;
    const startPos = el.dataset.start || "top 80%";
    gsap.to(el, {
      scrollTrigger: { trigger: el, start: startPos },
      opacity: 1,
      x: 0,
      duration: 0.8,
      ease: "power2.out",
    });
  });
}

/** 一文字ずつ出現（下から・once）SPのみ改行（<br>）対応 */
export function initCharsReveal({ scope } = {}) {
  if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
  }

  const mqSP = window.matchMedia("(max-width: 768px)");

  const wrapLinesByBR_SPOnly = (container) => {
    if (container.querySelector(":scope > .char-line")) return;

    const nodes = Array.from(container.childNodes);
    const lines = [];
    let current = [];

    const flush = () => {
      const line = document.createElement("span");
      line.className = "char-line";

      const inner = document.createElement("span");
      inner.className = "char-line__inner";

      current.forEach((n) => inner.appendChild(n));
      line.appendChild(inner);
      lines.push(line);
      current = [];
    };

    nodes.forEach((node) => {
      const isBR = node.nodeType === 1 && node.nodeName === "BR";
      if (isBR) flush();
      else current.push(node);
    });
    flush();

    container.innerHTML = "";
    lines.forEach((line) => container.appendChild(line));
  };

  $$(scope, ".js-chars").forEach((el) => {
    if (!guardOnce(el, "chars")) return;

    el.classList.remove("is-chars-ready");

    const target = el.querySelector("span") || el;

    splitIntoChars(target);

    if (mqSP.matches) wrapLinesByBR_SPOnly(target);

    const chars = target.querySelectorAll(".char");
    if (!chars.length) return;

    gsap.set(chars, { yPercent: 110 });

    el.classList.add("is-chars-ready");

    gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top 70%",
        once: true,
      },
    }).to(chars, {
      yPercent: 0,
      duration: 0.55,
      ease: "power1.out",
      stagger: { each: 0.03, from: "start" },
    });
  });
}


/** Split open vertically（center -> up/down, + scale） */
export function initSplitY({ scope } = {}) {
  $$(scope, ".js-split-y").forEach((el) => {
    if (!guardOnce(el, "splitY")) return;

    const FROM = "inset(50% 0 50% 0)";
    const TO = "inset(0% 0% 0% 0%)";

    el.style.clipPath = FROM;
    el.style.setProperty("-webkit-clip-path", FROM);
    el.style.scale = "0.98";

    gsap.to(el, {
      scrollTrigger: { trigger: el, start: "top 80%", once: true },
      clipPath: TO,
      scale: 1,
      duration: 1.1,
      ease: "power2.out",
      onUpdate: () => {
        el.style.setProperty(
          "-webkit-clip-path",
          gsap.getProperty(el, "clipPath")
        );
      },
    });
  });
}

/** 方向に応じた from/to の clip-path を返す（ヘルパー） */
function resolveClipDirection(el) {
  if (el.classList.contains("js-clip-scroll--from-left")) {
    // 左→右
    return { from: "inset(0 0 0 100%)", to: "inset(0 0 0 0%)" };
  }
  if (el.classList.contains("js-clip-scroll--vertical")) {
    // 上→下
    return { from: "inset(100% 0 0 0)", to: "inset(0% 0 0 0)" };
  }
  // 右→左（デフォルト）
  return { from: "inset(0 100% 0 0)", to: "inset(0 0% 0 0)" };
}

/** 同期版（scrub）: スクロールに追従して表示 */
export function initClipScroll({ scope } = {}) {
  $$(scope, ".js-clip-scroll").forEach((el) => {
    if (!guardOnce(el, "clipScroll")) return;

    const { from, to } = resolveClipDirection(el);
    el.style.clipPath = from;
    el.style.setProperty("-webkit-clip-path", from);

    gsap.to(el, {
      clipPath: to,
      ease: "none",
      scrollTrigger: {
        trigger: el,
        start: "top 80%",
        end: "top 30%",
        scrub: true,
      },
      onUpdate: () => {
        el.style.setProperty(
          "-webkit-clip-path",
          gsap.getProperty(el, "clipPath")
        );
      },
    });
  });
}

/** Text Decode（ランダム数字→本来の文字） */
export function initTextDecode({ scope } = {}) {
  const groups = $$(scope, ".js-text-random");
  if (!groups.length) return;

  const randomChars = "0123456789";
  const duration = 1500;
  const interval = 25;
  const charDelay = duration * 0.1;

  groups.forEach((group) => {
    group.classList.add("is-decode-ready");
    const labels = group.querySelectorAll("span");
    labels.forEach((label) => {
      if (!guardOnce(label, "textDecode")) return;

      const text = (label.textContent || "").trim();
      label.textContent = "";

      [...text].forEach((char, j) => {
        const span = document.createElement("span");
        span.textContent =
          randomChars[Math.floor(Math.random() * randomChars.length)];
        label.appendChild(span);

        const totalDelay = j * charDelay;

        setTimeout(() => {
          const startTime = performance.now();

          const update = (now) => {
            const elapsed = now - startTime;
            if (elapsed < duration) {
              if (elapsed % interval < interval / 2) {
                span.textContent =
                  randomChars[Math.floor(Math.random() * randomChars.length)];
              }
              requestAnimationFrame(update);
            } else {
              span.textContent = char;
            }
          };

          requestAnimationFrame(update);
        }, totalDelay);
      });
    });
  });
}

/** Rise on load（CSS駆動・JSは初期化/補助のみ） */
export function initRiseOnLoad({ scope } = {}) {
  const els = $$(scope, ".js-rise-onload");
  if (!els.length) return;

  els.forEach((el) => {
    if (!guardOnce(el, "riseOnLoad")) return;

    el.offsetHeight;

    el.classList.add("is-rise-play");
  });
}

/* =========================
   Batch initializer (exported)
========================= */
export function initAnimations({ scope } = {}) {
  initFadeUp({ scope });
  initSlideIn({ scope });
  initCharsReveal({ scope });
  initSplitY({ scope });
  initClipScroll({ scope });
  initTextDecode({ scope });
  initRiseOnLoad({ scope });

  refreshST();
}

/* =========================
   Auto init on load (optional)
========================= */
window.addEventListener("load", () => {
  initAnimations({ scope: document });
});

/* =========================
   Finish hook
========================= */
export function refreshScrollTrigger() {
  refreshST();
}
